import type { FirebaseError } from 'firebase/app';
import {
  getAuthService,
  getFirestoreInstance,
} from '../../shared/services/firebaseConfig';
import {
  addDocument,
  batchWrite,
  generateDocId,
  generateTimestamp,
  getDocument,
  queryDocuments,
  setDocument,
  updateDocument,
} from '../../shared/services/firestoreUtilities';
import {
  AuthStateCallback,
  AuthUser,
  Membership,
  MembershipDoc,
  NormalizedUserDoc,
  UserInvite,
  UserInviteDoc,
  UserDoc,
  UserProfile,
} from './types';

import { SubscriptionStatus } from '../../shared/organization/types';
import { BatchUpdate } from '../../shared/services/firestoreTypes';
import { OrganizationDoc } from '../../shared/organization/types';
import { convertTimestamps } from '../../shared/utils';

export const saveUserProfileToFirestore = async (
  profile: Omit<AuthUser, 'createdAt' | 'updatedAt'>,
) => {
  try {
    if (!profile.uid) throw new Error('User UID is undefined');

    const firestore = await getFirestoreInstance();
    const timestamp = await generateTimestamp();

    const data = {
      ...profile,
      updatedAt: timestamp,
    };
    await setDocument(firestore, 'users', profile.uid, data);
  } catch (error) {
    console.error('Failed to save user to Firestore:', error);
  }
};

export const updateUserProfile = async (
  profile: Partial<UserDoc> & { uid: string },
): Promise<void> => {
  try {
    const firestore = await getFirestoreInstance();
    const timestamp = await generateTimestamp();
    const data = {
      ...profile,
      updatedAt: timestamp,
    };

    await updateDocument(firestore, 'users', profile.uid, data);
  } catch (error) {
    console.error('Failed to update user profile:', error);
  }
};

export const updateAuthProfile = async (
  profile: Partial<AuthUser>,
): Promise<void> => {
  try {
    const auth = await getAuthService();
    const { updateProfile } = await import('firebase/auth');
    const { currentUser } = auth;
    if (!currentUser) throw new Error('User not logged in');

    await updateProfile(currentUser, profile);
  } catch (error) {
    console.error('Failed to update user profile:', error);
  }
};

/**
 * Listens for Firebase Auth state changes and updates the callback
 * @param {AuthStateCallback} callback - Function to execute on auth state change
 * @returns {Function} Unsubscribe function
 */
export const listenForAuthChanges = async (callback: AuthStateCallback) => {
  const auth = await getAuthService();

  if (!auth) {
    console.error('Auth service is not initialized');
    return () => {};
  }
  const { onAuthStateChanged } = await import('firebase/auth');

  return onAuthStateChanged(auth, (user) => {
    if (user) {
      callback({
        uid: user.uid,
        firstName: user.displayName?.split(' ')[0] || '',
        lastName: user.displayName?.split(' ')[1] || '',
        displayName: user.displayName || '',
        email: user.email || '',
        photoURL: user.photoURL || '',
      });
    } else {
      callback(null);
    }
  });
};

/**
 * Logs in a user using Google Sign-In
 */
export const loginWithGoogle = async (): Promise<AuthUser> => {
  const auth = await getAuthService();
  const { signInWithPopup, GoogleAuthProvider } = await import('firebase/auth');
  const provider = new GoogleAuthProvider();
  const result = await signInWithPopup(auth, provider);
  const user = result.user;

  return {
    firstName: user.displayName?.split(' ')[0] || '',
    lastName: user.displayName?.split(' ')[1] || '',
    displayName: user.displayName || '',
    email: user.email || '',
    photoURL: user.photoURL || '',
    uid: user.uid,
  };
};

export const logout = async () => {
  try {
    const auth = await getAuthService();
    const { signOut } = await import('firebase/auth');
    await signOut(auth);
    console.log('User logged out successfully');
  } catch (error) {
    console.error('Logout failed:', error);
  }
};

export const getUserProfile = async (
  uid: string,
): Promise<NormalizedUserDoc | null> => {
  try {
    const firestore = await getFirestoreInstance();
    const doc = await getDocument<UserDoc>(firestore, 'users', uid);
    if (!doc) {
      return null;
    }
    const normalizedDoc = await convertTimestamps<UserDoc>(doc);
    return normalizedDoc;
  } catch (error) {
    console.error('Error getting user profile:', error);
    throw new Error('Error getting user profile');
  }
};

export const getMemberships = async (uid: string): Promise<Membership[]> => {
  try {
    const firestore = await getFirestoreInstance();
    const { where } = await import('firebase/firestore');
    const { documents } = await queryDocuments<MembershipDoc>(
      firestore,
      'memberships',
      where('uid', '==', uid),
    );

    const normalizedDocs = await Promise.all(
      documents.map((membership) =>
        convertTimestamps<MembershipDoc>(membership),
      ),
    );
    return normalizedDocs;
  } catch (error) {
    console.error('Error getting memberships:', error);
    return [];
  }
};

export const getMembership = async (uid: string, orgId: string) => {
  try {
    const firestore = await getFirestoreInstance();
    const doc = await getDocument<MembershipDoc>(
      firestore,
      'memberships',
      `${uid}_${orgId}`,
    );
    if (!doc) {
      return null;
    }
    const normalizedDoc = await convertTimestamps<MembershipDoc>(doc);
    return normalizedDoc;
  } catch (error) {
    console.error('Error getting membership:', error);
    return null;
  }
};

export const checkForInvite = async (email: string) => {
  try {
    const firestore = await getFirestoreInstance();
    const { where } = await import('firebase/firestore');
    const { documents } = await queryDocuments(
      firestore,
      'invites',
      where('email', '==', email),
    );

    if (documents) {
      return documents[0] as UserInvite;
    } else {
      return null;
    }
  } catch (error) {
    console.error('Error checking email:', error);
    return null;
  }
};

export const acceptInvite = async (inviteId: string, uid: string) => {
  try {
    const firestore = await getFirestoreInstance();
    const timestamp = await generateTimestamp();
    await updateDocument(firestore, 'invites', inviteId, {
      status: 'accepted',
      acceptedBy: uid,
      acceptedAt: timestamp,
    });
  } catch (error) {
    console.error('acceptInvite Error:', error);
  }
};

export const createInvite = async ({
  firstName,
  lastName,
  email,
  role,
  organizationId,
  invitedBy,
}: Partial<UserInvite>) => {
  try {
    const firestore = await getFirestoreInstance();
    const { serverTimestamp } = await import('firebase/firestore');

    await addDocument(firestore, 'invites', {
      firstName,
      lastName,
      displayName: `${firstName} ${lastName}`,
      email,
      role,
      organizationId,
      invitedBy,
      status: 'pending',
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    });
  } catch (error) {
    console.error('Error creating invite:', error);
    throw error;
  }
};

export const deleteInvite = async (inviteId: string) => {
  try {
    const firestore = await getFirestoreInstance();
    const { doc, deleteDoc } = await import('firebase/firestore');

    await deleteDoc(doc(firestore, 'invites', inviteId));
  } catch (error) {
    console.error('Error deleting invite:', error);
  }
};

export const createEmailUser = async (email: string, password: string) => {
  try {
    const auth = await getAuthService();
    const { createUserWithEmailAndPassword } = await import('firebase/auth');
    const { user } = await createUserWithEmailAndPassword(
      auth,
      email,
      password,
    );

    return {
      ...user,
      firstName: '',
      lastName: '',
      displayName: '',
    } as AuthUser;
  } catch (error) {
    console.error('Error creating user:', error);
    return null;
  }
};

export const loginWithEmailAndPassword = async (
  email: string,
  password: string,
): Promise<UserProfile | null> => {
  try {
    const auth = await getAuthService();
    const { signInWithEmailAndPassword } = await import('firebase/auth');
    const { user } = await signInWithEmailAndPassword(auth, email, password);
    const userProfile = await getUserProfile(user.uid);
    const memberships = await getMemberships(user.uid);
    if (!userProfile) throw new Error('User profile not found');
    return { ...userProfile, memberships };
  } catch (error) {
    const { FirebaseError } = await import('firebase/app');
    console.error('Error logging in:', error);
    if (error instanceof FirebaseError && 'code' in error) {
      const message = getFriendlyAuthError((error as FirebaseError).code);
      throw new Error(message);
    }
    return null;
  }
};

export const handleOpenSignup = async (user: AuthUser) => {
  const firestore = await getFirestoreInstance();
  try {
    const orgId = await generateDocId(firestore, 'organizations');
    const timestamp = await generateTimestamp();

    const userProfile: UserDoc = {
      uid: user.uid,
      firstName: user?.displayName?.split(' ')[0] || '',
      lastName: user?.displayName?.split(' ')[1] || '',
      displayName: user?.displayName || '',
      email: user?.email || '',
      photoURL: user?.photoURL || '',
      createdAt: timestamp,
      updatedAt: timestamp,
    };

    const orgProfile: OrganizationDoc = {
      id: orgId,
      name: 'My Organization',
      slug: 'my-organization',
      ownerId: user.uid,
      createdAt: timestamp,
      updatedAt: timestamp,
      subscriptionStatus: 'free' as SubscriptionStatus,
      userLimit: 5,
      stripeCustomerId: '',
      stripeSubscriptionId: '',
      overLimit: false,
    };

    const membership: MembershipDoc = {
      uid: user.uid,
      organizationId: orgId,
      displayName: user?.displayName || '',
      role: 'owner',
      createdAt: timestamp,
      updatedAt: timestamp,
    };

    const batchUpdates = [
      {
        type: 'set',
        collectionPath: 'users',
        id: user.uid,
        data: userProfile,
      } satisfies BatchUpdate<UserDoc>,
      {
        type: 'set',
        collectionPath: 'organizations',
        id: orgId,
        data: orgProfile,
      } satisfies BatchUpdate<OrganizationDoc>,
      {
        type: 'set',
        collectionPath: 'memberships',
        id: `${user.uid}_${orgId}`,
        data: membership,
      } satisfies BatchUpdate<MembershipDoc>,
    ];

    await batchWrite(firestore, batchUpdates);

    return {
      userProfile: await convertTimestamps(userProfile),
      orgProfile: await convertTimestamps(orgProfile),
      memberships: [await convertTimestamps(membership)],
    };
  } catch (error) {
    console.error('handleOpenSignup Error:', error);
    throw error;
  }
};

export const handleInvitedSignup = async (
  user: AuthUser,
  invite: UserInvite,
) => {
  const firestore = await getFirestoreInstance();
  const timestamp = await generateTimestamp();

  try {
    const userProfile: UserDoc = {
      uid: user.uid,
      firstName: user?.displayName?.split(' ')[0] || '',
      lastName: user?.displayName?.split(' ')[1] || '',
      displayName: user?.displayName || '',
      email: user?.email || '',
      photoURL: user?.photoURL || '',
      createdAt: timestamp,
      updatedAt: timestamp,
    };

    const membership: MembershipDoc = {
      uid: user.uid,
      organizationId: invite.organizationId,
      displayName: user?.displayName || '',
      role: invite.role,
      createdAt: timestamp,
      updatedAt: timestamp,
    };

    const inviteUpdate: Partial<UserInviteDoc> = {
      status: 'accepted',
      acceptedBy: user.uid,
      updatedAt: timestamp,
    };

    const batchUpdates = [
      {
        type: 'update',
        collectionPath: 'invites',
        id: invite.id,
        data: inviteUpdate,
      } satisfies BatchUpdate<Partial<UserInviteDoc>>,
      {
        type: 'set',
        collectionPath: 'users',
        id: user.uid,
        data: userProfile,
      } satisfies BatchUpdate<UserDoc>,
      {
        type: 'set',
        collectionPath: 'memberships',
        id: `${user.uid}_${invite.organizationId}`,
        data: membership,
      } satisfies BatchUpdate<MembershipDoc>,
    ];

    await batchWrite(firestore, batchUpdates);

    return {
      ...(await convertTimestamps(userProfile)),
      memberships: [await convertTimestamps(membership)],
    };
  } catch (error) {
    console.error('handleInvitedSignup Error:', error);
    throw error;
  }
};

export function getFriendlyAuthError(errorCode: string): string {
  const map: Record<string, string> = {
    'auth/user-not-found':
      'User not found. Please check your details or create an account.',
    'auth/wrong-password': 'Incorrect password. Please try again.',
    'auth/email-already-in-use': 'This email is already in use.',
    'auth/invalid-email': 'Invalid email address.',
    'auth/weak-password':
      'Password is too weak. Please use a stronger password.',
    'auth/network-request-failed': 'Network error. Please try again later.',
    'auth/too-many-requests':
      'Too many attempts. Please wait and try again later.',
  };

  return map[errorCode] || 'Something went wrong. Please try again.';
}

export const resetPassword = async (email: string) => {
  try {
    const auth = await getAuthService();
    const { sendPasswordResetEmail } = await import('firebase/auth');
    await sendPasswordResetEmail(auth, email);
  } catch (error) {
    console.error('Error sending password reset email:', error);
  }
};
